import { Divider, Drawer, DrawerBody, Text } from "@aurem/ui";
import { RolePicker } from "components/Topbar/RolePicker";
import ROUTES from "const/routes";
import { useNavigation } from "hooks/navigate";
import { useTermsDocumentUrl } from "hooks/useTermsDocumentUrl";
import {
  ChevronRightIcon,
  FileTextIcon,
  LogOutIcon,
  SettingsIcon,
} from "lucide-react";
import { useI18n } from "providers/I18nProvider";
import { useCallback } from "react";
import { useSignOut } from "utils/auth";
import { useUserData } from "../../graphql/user";
import { useCurrentApp } from "../../hooks/useCurrentApp";

type Props = {
  open: boolean;
  onClose: () => void;
};

export const UserDrawer = ({ open, onClose }: Props) => {
  const { t, language } = useI18n();
  const navigate = useNavigation();
  const signOut = useSignOut();
  const { user } = useUserData();
  const termsUrl = useTermsDocumentUrl(language);

  const currentApp = useCurrentApp();
  const isAdminRoute = currentApp === "admin";

  const handleSignOut = useCallback(() => {
    void signOut();
    onClose();
  }, [signOut, onClose]);

  const handlePersonalSettings = useCallback(() => {
    if (isAdminRoute) {
      void navigate(`/${ROUTES.PROFILE}`);
    } else {
      void navigate(`/${language}/employee/settings/account-details`);
    }
    onClose();
  }, [navigate, language, isAdminRoute, onClose]);

  const handleTermsAndConditions = useCallback(() => {
    if (termsUrl) {
      window.open(termsUrl, "_blank", "noopener,noreferrer");
    }
    onClose();
  }, [onClose, termsUrl]);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        onClose();
      }
    },
    [onClose],
  );

  if (!user) return null;

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerBody className="flex flex-col gap-2 p-0">
        <RolePicker user={user} onRoleSelect={onClose} />

        <Divider direction="horizontal" />

        <nav className="flex flex-col p-6 pb-4 pt-2">
          <button
            onClick={handlePersonalSettings}
            className="flex items-center gap-3 h-14 text-start"
          >
            <span className="flex items-center justify-center w-8 h-8 shrink-0 rounded-md bg-neutral-50">
              <SettingsIcon
                size="1.25rem"
                className="rtl:-scale-x-100 text-neutral-200"
              />
            </span>
            <Text size="md" className="text-neutral-500">
              {t("apps.admin.navBar.personalSettings")}
            </Text>
            <ChevronRightIcon className="rtl:-scale-x-100 ms-auto text-neutral-200" />
          </button>

          <Divider />

          {!isAdminRoute && termsUrl && (
            <>
              <button
                onClick={handleTermsAndConditions}
                className="flex items-center gap-3 h-14 text-start"
              >
                <span className="flex items-center justify-center w-8 h-8 shrink-0 rounded-md bg-neutral-50">
                  <FileTextIcon
                    size="1.25rem"
                    className="rtl:-scale-x-100 text-neutral-200"
                  />
                </span>
                <Text size="md" className="text-neutral-500">
                  {t("apps.employee.navigation.termsAndConditions")}
                </Text>
                <ChevronRightIcon className="rtl:-scale-x-100 ms-auto text-neutral-200" />
              </button>

              <Divider />
            </>
          )}

          <button
            onClick={handleSignOut}
            className="flex items-center gap-3 h-14 text-start"
          >
            <span className="flex items-center justify-center w-8 h-8 shrink-0 rounded-md bg-neutral-50">
              <LogOutIcon
                size="1.25rem"
                className="rtl:-scale-x-100 text-neutral-200"
              />
            </span>
            <Text size="md" className="text-neutral-500">
              {t("apps.admin.common.signOut")}
            </Text>
            <ChevronRightIcon className="rtl:-scale-x-100 ms-auto text-neutral-200" />
          </button>
        </nav>
      </DrawerBody>
    </Drawer>
  );
};
